import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Clock, CheckCircle, Truck, PackageCheck, XCircle, MapPin, PhoneCall, Mail, Store } from "lucide-react";
import { motion } from "framer-motion";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface RepairOrder {
  id: string;
  order_number: number;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const UserRepairOrders = () => {
  const [repairs, setRepairs] = useState<RepairOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t, isHebrew } = useStaticTranslation("repairOrders");

  const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock; description: string }> = {
    pending: { label: t("pending", "ממתין"), color: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: Clock, description: t("pendingDesc", "ההזמנה שלך התקבלה וממתינה לטיפול") },
    in_repair: { label: t("inRepair", "בתיקון"), color: "bg-blue-500/10 text-blue-600 border-blue-500/20", icon: Package, description: t("inRepairDesc", "התכשיט שלך נמצא כרגע בתהליך תיקון") },
    waiting_in_store: { label: t("waitingInStore", "מחכה בחנות"), color: "bg-orange-500/10 text-orange-600 border-orange-500/20", icon: Store, description: t("waitingInStoreDesc", "התיקון הושלם! התכשיט מחכה לך בחנות") },
    completed: { label: t("completed", "הושלם"), color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", icon: CheckCircle, description: t("completedDesc", "התיקון הושלם!") },
    shipped: { label: t("shipped", "נשלח"), color: "bg-violet-500/10 text-violet-600 border-violet-500/20", icon: Truck, description: t("shippedDesc", "ההזמנה נשלחה אליך") },
    delivered: { label: t("delivered", "נמסר"), color: "bg-green-500/10 text-green-600 border-green-500/20", icon: PackageCheck, description: t("deliveredDesc", "ההזמנה נמסרה בהצלחה") },
    cancelled: { label: t("cancelled", "בוטל"), color: "bg-red-500/10 text-red-600 border-red-500/20", icon: XCircle, description: t("cancelledDesc", "ההזמנה בוטלה") },
  };

  const STATUS_OPTIONS = [
    { value: "pending", label: t("pending", "ממתין") },
    { value: "in_repair", label: t("inRepair", "בתיקון") },
    { value: "waiting_in_store", label: t("waitingInStore", "מחכה בחנות") },
    { value: "delivered", label: t("delivered", "נמסר") },
  ];

  const getStatusStep = (status: string) => {
    const steps = ["pending", "in_repair", "waiting_in_store", "delivered"];
    return steps.indexOf(status) + 1;
  };

  useEffect(() => {
    loadRepairs();
  }, []);

  const loadRepairs = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("phone")
        .eq("id", session.user.id)
        .single();

      const userPhone = profile?.phone;

      let query = supabase
        .from("repair_orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (userPhone) {
        query = query.or(`user_id.eq.${session.user.id},guest_phone.eq.${userPhone}`);
      } else {
        query = query.eq("user_id", session.user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRepairs(data || []);
    } catch (error) {
      console.error("Error loading repairs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isHebrew ? "he-IL" : "en-US", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status] || {
      label: status, color: "bg-muted text-muted-foreground", icon: Package, description: ""
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (repairs.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="border border-border/50 shadow-none">
          <CardContent className="py-16 text-center">
            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <Package className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t("noOrders", "לא נמצאו הזמנות")}</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {t("noOrdersDesc", "כאשר תהיה לך הזמנה או תיקון פעיל, תוכל לעקוב אחריו כאן")}
            </p>
          </CardContent>
        </Card>
        <StoreLocationCard t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold">{t("yourOrders", "ההזמנות שלך")}</h2>
        <p className="text-muted-foreground text-sm">{repairs.length} {t("ordersFound", "הזמנות נמצאו")}</p>
      </div>

      <div className="space-y-4">
        {repairs.map((repair, index) => {
          const statusConfig = getStatusConfig(repair.status);
          const StatusIcon = statusConfig.icon;

          return (
            <motion.div
              key={repair.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="border border-border/50 shadow-none overflow-hidden">
                <CardContent className="p-0">
                  {repair.status !== "cancelled" && (
                    <div className="bg-muted/50 p-4 border-b">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                        {STATUS_OPTIONS.map((s) => (
                          <span key={s.value} className={repair.status === s.value ? "font-medium text-primary" : ""}>
                            {s.label}
                          </span>
                        ))}
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${(getStatusStep(repair.status) / 4) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="p-4 md:p-6 space-y-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 rounded-full ${statusConfig.color} shrink-0`}>
                          <StatusIcon className="h-5 w-5" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{t("requestNumber", "פנייה מספר")}</span>
                            <span className="font-bold text-xl">#{repair.order_number}</span>
                          </div>
                          <h3 className="text-lg font-semibold">{repair.title}</h3>
                          <p className="text-sm text-primary/80">{statusConfig.description}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`${statusConfig.color} border font-medium shrink-0`}>
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {repair.description && (
                      <p className="text-muted-foreground bg-muted/50 p-3 rounded-lg text-sm">{repair.description}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pt-4 border-t">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{t("created", "נוצר:")} {formatDate(repair.created_at)}</span>
                      </div>
                      {repair.updated_at !== repair.created_at && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{t("updated", "עודכן:")} {formatDate(repair.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <StoreLocationCard t={t} />
    </div>
  );
};

const StoreLocationCard = ({ t }: { t: (key: string, fallback: string) => string }) => (
  <Card className="border border-border/50 shadow-none overflow-hidden">
    <CardContent className="p-0">
      <div className="grid md:grid-cols-2">
        <div className="h-64 md:h-auto md:min-h-[300px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3411.261797287465!2d34.79208012489179!3d31.241175260883782!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x150266113eb89b75%3A0xa6850d035d15c6e8!2z15DXnNeZ16EgfCDXqteb16nXmdeY15nXnSDXldep16fXldeg15nXnQ!5e0!3m2!1siw!2sil!4v1767350674850!5m2!1siw!2sil"
            width="100%" height="100%" style={{ border: 0, minHeight: "250px" }}
            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            title={t("storeLocation", "מיקום החנות")}
          />
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div>
            <h3 className="text-xl font-bold mb-2">{t("storePickup", "איסוף מהחנות")}</h3>
            <p className="text-muted-foreground">{t("storePickupDesc", "ניתן לאסוף את ההזמנה מהחנות שלנו בשעות הפעילות")}</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("address", "כתובת")}</p>
                <p className="text-muted-foreground">{t("addressValue", "הרצל 58, באר שבע")}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <PhoneCall className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("phone", "טלפון")}</p>
                <a href="tel:0795575576" className="text-muted-foreground hover:text-primary transition-colors" dir="ltr">
                  0795575576
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("businessHours", "שעות פעילות")}</p>
                <div className="text-muted-foreground text-sm space-y-1">
                  <p>{t("sunday", "ראשון: 10:00-19:00")}</p>
                  <p>{t("monday", "שני: 10:00-19:00")}</p>
                  <p>{t("tuesday", "שלישי: 09:45-14:00")}</p>
                  <p>{t("wednesday", "רביעי: 10:00-19:00")}</p>
                  <p>{t("thursday", "חמישי: 10:00-19:00")}</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("email", "אימייל")}</p>
                <a href="mailto:info@alice-jewelry.co.il" className="text-muted-foreground hover:text-primary transition-colors">
                  info@alice-jewelry.co.il
                </a>
              </div>
            </div>
          </div>

          <Button variant="outline" className="w-full" asChild>
            <a href="https://waze.com/ul?ll=31.241175,34.794269&navigate=yes" target="_blank" rel="noopener noreferrer">
              <MapPin className="ml-2 h-4 w-4" />
              {t("navigateWaze", "נווט עם Waze")}
            </a>
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);
